import React, { useLayoutEffect, useRef, useState } from 'react';
import styles from './segmented-tabs.module.css';

/**
 * SegmentedTabs — joined tabs with a sliding indicator, for switching between
 * views of the same page.
 *
 * Props:
 *   options  — Array<{ value: string, label: string }>
 *   value    — string  The active tab's value.
 *   onChange — (value: string) => void  Called only when a different tab is picked.
 *   label    — string  Accessible name for the tab list.
 *
 * Usage:
 *   <SegmentedTabs
 *     options={[{ value: 'campaign', label: 'Campaigns' }, { value: 'transactional', label: 'Transactional' }]}
 *     value={type}
 *     onChange={setType}
 *   />
 */
export const SegmentedTabs = ({ options = [], value, onChange = () => {}, label }) => {
  const track_ref = useRef(null);
  const tab_refs = useRef([]);
  // Tabs size to their content, so the highlight is measured from the active tab.
  const [indicator, setIndicator] = useState(null);
  const active_index = Math.max(
    options.findIndex((option) => option.value === value),
    0
  );

  useLayoutEffect(() => {
    const measure = () => {
      const tab = tab_refs.current[active_index];
      if (!tab) return;
      setIndicator({ left: tab.offsetLeft, width: tab.offsetWidth });
    };

    measure();
    // Re-measure when the tabs resize (e.g. web fonts load or labels change).
    if (typeof ResizeObserver === 'undefined' || !track_ref.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(track_ref.current);
    return () => observer.disconnect();
  }, [active_index, options]);

  const select = (index, focus = false) => {
    const option = options[index];
    if (!option) return;
    if (focus) tab_refs.current[index]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  const handleKeyDown = (event, index) => {
    const last = options.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select(next, true);
  };

  return (
    <div ref={track_ref} className={styles.segmented} role="tablist" aria-label={label}>
      {indicator && (
        <span
          className={styles.indicator}
          style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
          aria-hidden
        />
      )}
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(el) => {
            tab_refs.current[index] = el;
          }}
          type="button"
          role="tab"
          aria-selected={index === active_index}
          tabIndex={index === active_index ? 0 : -1}
          className={styles.option}
          onClick={() => select(index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
